import React, { useMemo, useState } from 'react';
import { StreamEvent } from './AppEditor';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { ActionGroup, Action } from './ActionGroup';
import ReactMarkdown from 'react-markdown';
import { RefreshCw } from 'lucide-react';
import MarkdownContent from './MarkdownContent';
import { VibeText } from './VibeText';
import { SecretInput } from './SecretInput';

// BlockStore types imported from AppEditor component context
interface Block {
  id: string;
  type: 'text' | 'reasoning' | 'tool' | 'error';
  role?: 'user' | 'assistant';
  content: string;
  isComplete: boolean;
  isStreaming?: boolean; // For incomplete tool blocks (e.g., shell_exec)
  // For tool blocks
  toolCallId?: string;
  toolName?: string;
  toolInput?: any;
  toolOutput?: any;
  toolIsError?: boolean;
  // For reasoning blocks
  reasoningContent?: string;
}

interface BlockStore {
  blocks: Map<string, Block>;
  pendingToolCalls: Map<string, any>;
  latestError?: {
    id: string;
    content: string;
    errorType?: string;
  };
}

interface MessageListProps {
  messages?: StreamEvent[];
  blockStore?: BlockStore;
  onTryAgain?: () => void;
  applicationId?: string;
  onSendMessage?: (message: string, model: string) => void;
}

interface MessageBlock {
  id: string;
  type: 'user' | 'assistant' | 'action-group' | 'error';
  content?: string;
  errorType?: string;
  actions?: Action[];
  isStreaming?: boolean;
  currentStreamingAction?: string;
  isComplete: boolean;
}

// Get tool display name
function getToolDisplayName(toolName: string, input: any): string {
  switch (toolName) {
    case 'write_file':
      return input?.path ? `Write: ${input.path}` : '';
    case 'view_file':
      return input?.path ? `Read: ${input.path}` : '';
    case 'str_replace':
      return input?.path ? `Edit: ${input.path}` : '';
    case 'delete_file':
      return input?.path ? `Delete: ${input.path}` : '';
    case 'list_files':
      return input?.path ? `List: ${input.path}` : '';
    case 'shell_exec':
      return input?.command ? `$ ${input.command}` : '';
    case 'search':
      return input?.query ? `Search: ${input.query}` : '';
    case 'todos':
      return ''; // Hide todos tool
    default:
      return toolName.replace(/_/g, ' ');
  }
}

// Strip system reminder tags from text content
function stripReminderTags(text: string): string {
  return text
    .replace(/<system-reminder[^>]*>.*?<\/system-reminder>/gs, '')
    .replace(/<reminder[^>]*>.*?<\/reminder>/gs, '')
    .trim();
}

// Format error message based on error type
function formatErrorMessage(content: string, errorType?: string): string {
  switch (errorType) {
    case 'ratelimited':
      return 'AI provider is rate limiting requests';
    case 'overloaded':
      return 'AI provider is currently overloaded. Please try again in a moment';
    case 'internal':
      return `Server error: ${content}`;
    case 'interrupt':
      return 'User interrupted';
    default:
      return content;
  }
}

// Parse secret request tags from text content
interface SecretRequest {
  key: string;
  placeholder: string;
}

function parseSecretRequests(text: string): {
  parts: Array<{ type: 'text' | 'secret'; content: string; secretKey?: string }>;
} {
  const regex = /<secret-request\s+key="([^"]+)"\s*\/>/g;
  const parts: Array<{ type: 'text' | 'secret'; content: string; secretKey?: string }> = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex, match.index),
      });
    }

    // Add the secret request
    parts.push({
      type: 'secret',
      content: match[0],
      secretKey: match[1],
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(lastIndex),
    });
  }

  // If no secret requests found, return the original text as a single part
  if (parts.length === 0) {
    parts.push({ type: 'text', content: text });
  }

  return { parts };
}

export const MessageList: React.FC<MessageListProps> = ({ messages, blockStore, onTryAgain, applicationId, onSendMessage }) => {
  const blocks = useMemo(() => {
    const result: MessageBlock[] = [];
    let actionGroupCounter = 0;
    
    // Use BlockStore if provided (new implementation)
    if (blockStore) {
      // Get all COMPLETE blocks and INCOMPLETE shell_exec blocks, sorted by UUID v7 (timestamp-ordered)
      // Use simple string comparison, not localeCompare (which has locale-specific rules)
      const completeBlocks = Array.from(blockStore.blocks.values())
        .filter(block => block.isComplete || (block.toolName === 'shell_exec' && block.isStreaming))
        .sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
      
      // Process blocks in order - group consecutive actions, keep everything else in order
      let currentActionGroup: MessageBlock | null = null;
      let currentAssistantMessage: MessageBlock | null = null;
      let lastRole: string | undefined = undefined;
      
      for (const block of completeBlocks) {
        if (block.type === 'text' && block.content.trim()) {
          // Close any open action group
          if (currentActionGroup) {
            currentActionGroup = null;
          }
          
          const currentRole = block.role || 'assistant';
          
          // Check if we should merge with the previous assistant message
          if (currentRole === 'assistant' && lastRole === 'assistant' && currentAssistantMessage) {
            // Simply concatenate consecutive assistant text blocks
            // The API already includes proper formatting (spaces, newlines, etc.) in the text
            currentAssistantMessage.content = currentAssistantMessage.content + block.content;
          } else {
            // Start a new message block
            if (currentRole === 'assistant') {
              currentAssistantMessage = {
                id: `text-${block.id}`,
                type: 'assistant',
                content: block.content,
                isComplete: true
              };
              result.push(currentAssistantMessage);
            } else {
              // User message - always create new block
              currentAssistantMessage = null;
              result.push({
                id: `text-${block.id}`,
                type: 'user',
                content: block.content,
                isComplete: true
              });
            }
          }
          
          lastRole = currentRole;
        }
        else if (block.type === 'reasoning' && block.reasoningContent?.trim()) {
          // Reset assistant message grouping when we hit an action
          currentAssistantMessage = null;
          lastRole = undefined;
          
          // Start or continue action group
          if (!currentActionGroup) {
            actionGroupCounter++;
            currentActionGroup = {
              id: `action-group-${actionGroupCounter}`,
              type: 'action-group',
              actions: [],
              isStreaming: false,
              isComplete: true
            };
            result.push(currentActionGroup);
          }
          
          currentActionGroup.actions!.push({
            id: block.id,
            type: 'reasoning',
            description: 'Thinking...',
            reasoningContent: block.reasoningContent
          });
        }
        else if (block.type === 'tool' && block.toolName) {
          const description = getToolDisplayName(block.toolName, block.toolInput);
          if (description) {
            // Reset assistant message grouping when we hit an action
            currentAssistantMessage = null;
            lastRole = undefined;
            
            // Start or continue action group
            if (!currentActionGroup) {
              actionGroupCounter++;
              currentActionGroup = {
                id: `action-group-${actionGroupCounter}`,
                type: 'action-group',
                actions: [],
                isStreaming: false,
                isComplete: true
              };
              result.push(currentActionGroup);
            }

            currentActionGroup.actions!.push({
              id: block.id,
              type: 'tool',
              toolName: block.toolName,
              description,
              toolInput: block.toolInput,
              toolOutput: block.toolOutput,
              toolIsError: block.toolIsError,
              isStreaming: block.isStreaming
            });
          }
        }
      }
      
      // Add the latest error at the end if present
      if (blockStore.latestError) {
        result.push({
          id: blockStore.latestError.id,
          type: 'error',
          content: blockStore.latestError.content,
          errorType: blockStore.latestError.errorType,
          isComplete: true
        });
      }
      
      return result;
    }
    
    // Fall back to old implementation using messages array
    if (!messages) return result;
    
    // State tracking for old implementation
    let currentRole: string | null = null;
    let currentActionGroupId: string | null = null;
    let currentActionGroup: MessageBlock | null = null;
    let currentTextBlock: MessageBlock | null = null;
    let currentStreamingActionId: string | null = null;
    
    // Track pending tool info until output is available
    const pendingTools = new Map<string, { toolName?: string; toolInput?: any }>();

    messages.forEach((event) => {
      // Handle error events
      if (event.type === 'error') {
        // Complete any pending blocks
        if (currentTextBlock) {
          currentTextBlock.isComplete = true;
          currentTextBlock = null;
        }
        if (currentActionGroup) {
          currentActionGroup.isComplete = true;
          currentActionGroup.isStreaming = false;
          currentActionGroup = null;
          currentActionGroupId = null;
        }
        
        result.push({
          id: `error-${Date.now()}-${Math.random()}`,
          type: 'error',
          content: event.error || 'An unknown error occurred',
          isComplete: true,
        });
        return;
      }

      // Handle text blocks
      if (event.type === 'text-start') {
        const blockId = `text-${event.id}`;
        
        // Complete any current action group if switching from assistant actions to text
        if (currentRole === 'assistant' && currentActionGroup) {
          currentActionGroup.isComplete = true;
          currentActionGroup.isStreaming = false;
          currentActionGroup = null;
          currentActionGroupId = null;
        }
        
        // Store the new text block info but don't add to result yet
        currentRole = event.role || 'assistant';
        currentTextBlock = {
          id: blockId,
          type: currentRole === 'user' ? 'user' : 'assistant',
          content: '',
          isComplete: false,
        };
        
      } else if (event.type === 'text-delta') {
        if (currentTextBlock) {
          currentTextBlock.content = (currentTextBlock.content || '') + (event.delta || '');
        }
      } else if (event.type === 'text-end') {
        if (currentTextBlock) {
          // Strip reminder tags from user messages
          if (currentTextBlock.type === 'user') {
            currentTextBlock.content = stripReminderTags(currentTextBlock.content || '');
          }
          currentTextBlock.isComplete = true;
          // Only add to result if not empty
          if (currentTextBlock.content?.trim()) {
            result.push(currentTextBlock);
          }
          currentTextBlock = null;
        }
      }

      // Handle reasoning blocks
      if (event.type === 'reasoning-start') {
        // Create or get current action group
        if (!currentActionGroup) {
          actionGroupCounter++;
          currentActionGroupId = `action-group-${actionGroupCounter}`;
          currentActionGroup = {
            id: currentActionGroupId,
            type: 'action-group',
            actions: [],
            isStreaming: true,
            isComplete: false,
          };
          result.push(currentActionGroup);
        }
        
        const actionId = `reasoning-${event.id}`;
        const action: Action = {
          id: actionId,
          type: 'reasoning',
          description: 'Thinking...',
          reasoningContent: '',
        };
        currentActionGroup.actions!.push(action);
        currentStreamingActionId = actionId;
        currentActionGroup.currentStreamingAction = actionId;
        currentActionGroup.isStreaming = true;
        
      } else if (event.type === 'reasoning-delta') {
        if (currentActionGroup) {
          const actionId = `reasoning-${event.id}`;
          const action = currentActionGroup.actions?.find(a => a.id === actionId);
          if (action) {
            action.reasoningContent = (action.reasoningContent || '') + (event.delta || '');
          }
        }
      } else if (event.type === 'reasoning-end') {
        if (currentActionGroup) {
          const actionId = `reasoning-${event.id}`;
          if (currentActionGroup.currentStreamingAction === actionId) {
            currentActionGroup.currentStreamingAction = undefined;
            // Check if there are no more streaming actions
            if (!currentActionGroup.currentStreamingAction) {
              currentActionGroup.isStreaming = false;
            }
          }
          currentStreamingActionId = null;
        }
      }

      // Handle tool calls
      if (event.type === 'tool-input-start') {
        // Store tool info for later
        const actionId = `tool-${event.toolCallId}`;
        pendingTools.set(actionId, { toolName: event.toolName });
        currentStreamingActionId = actionId;
        
      } else if (event.type === 'tool-input-available') {
        // Update the pending tool with input
        const actionId = `tool-${event.toolCallId}`;
        const pending = pendingTools.get(actionId);
        if (pending) {
          pending.toolInput = event.toolInput || event.input;
        }
        
      } else if (event.type === 'tool-output-available') {
        const actionId = `tool-${event.toolCallId}`;
        const pending = pendingTools.get(actionId);
        
        if (pending && pending.toolName && pending.toolInput) {
          const toolOutput = event.toolOutput || event.output;
          const toolIsError = event.toolIsError;
          const description = getToolDisplayName(pending.toolName, pending.toolInput);
          
          // Only add the action now that we have both input and output
          if (description) {
            // Create or get current action group
            if (!currentActionGroup) {
              actionGroupCounter++;
              currentActionGroupId = `action-group-${actionGroupCounter}`;
              currentActionGroup = {
                id: currentActionGroupId,
                type: 'action-group',
                actions: [],
                isStreaming: false,
                isComplete: false,
              };
              result.push(currentActionGroup);
            }
            
            const action: Action = {
              id: actionId,
              type: 'tool',
              toolName: pending.toolName,
              description: description,
              toolInput: pending.toolInput,
              toolOutput: toolOutput,
              toolIsError: toolIsError,
            };
            currentActionGroup.actions!.push(action);
          }
          
          // Clean up
          pendingTools.delete(actionId);
        }
        currentStreamingActionId = null;
      }
    });

    // Handle any remaining text block that wasn't completed
    if (currentTextBlock) {
      const textBlock = currentTextBlock as MessageBlock;
      if (textBlock.content?.trim()) {
        textBlock.isComplete = true;
        result.push(textBlock);
      }
    }
    if (currentActionGroup) {
      const actionGroup = currentActionGroup as MessageBlock;
      // If we're still streaming, keep it as streaming
      const lastEvent = messages[messages.length - 1];
      const isStillStreaming = lastEvent && 
        !lastEvent.type?.endsWith('-end') && 
        !lastEvent.type?.includes('output') && 
        !lastEvent.type?.includes('available');
      
      if (!isStillStreaming) {
        actionGroup.isComplete = true;
        actionGroup.isStreaming = false;
        actionGroup.currentStreamingAction = undefined;
      }
    }
    
    return result;
  }, [messages, blockStore]);

  return (
    <div className="space-y-6 text-xs font-mono" style={{ letterSpacing: '0.05em' }}>
      {blocks.map((block, index) => (
        <MessageBlock
          key={block.id}
          block={block}
          onTryAgain={onTryAgain}
          isLastError={block.type === 'error' && index === blocks.length - 1}
          applicationId={applicationId}
          onSendMessage={onSendMessage}
        />
      ))}
    </div>
  );
};

const MessageBlock: React.FC<{
  block: MessageBlock;
  onTryAgain?: () => void;
  isLastError?: boolean;
  applicationId?: string;
  onSendMessage?: (message: string, model: string) => void;
}> = ({
  block,
  onTryAgain,
  isLastError,
  applicationId,
  onSendMessage
}) => {
  if (block.type === 'action-group') {
    return (
      <div data-msg-id={block.id}>
        <ActionGroup
          actions={block.actions || []}
          isStreaming={block.isStreaming}
          currentStreamingAction={block.currentStreamingAction}
        />
      </div>
    );
  }

  if (block.type === 'user') {
    return (
      <Card className="w-full bg-gray-200 text-gray-900 rounded px-4 py-2" data-msg-id={block.id}>
        <MarkdownContent content={block.content || ''} variant="user" />
      </Card>
    );
  }

  if (block.type === 'assistant') {
    // Remove trailing colon from content
    let content = block.content || '';
    if (content.endsWith(':')) {
      content = content.slice(0, -1);
    }

    // Parse for secret requests
    const { parts } = parseSecretRequests(content);

    // If no secret requests, render normally
    if (parts.length === 1 && parts[0].type === 'text') {
      return (
        <div data-msg-id={block.id}>
          <MarkdownContent content={content} variant="agent" />
        </div>
      );
    }

    // Render with secret request components inline
    return (
      <div data-msg-id={block.id}>
        {parts.map((part, index) => {
          if (part.type === 'text') {
            return (
              <MarkdownContent
                key={index}
                content={part.content}
                variant="agent"
              />
            );
          } else if (part.type === 'secret' && part.secretKey && applicationId) {
            return (
              <SecretInput
                key={index}
                secretKey={part.secretKey}
                applicationId={applicationId}
                onSecretSet={(key) => {
                  // Send a message to continue the agent after secret is set
                  if (onSendMessage) {
                    onSendMessage(`I've set the secret "${key}". Please continue.`, 'claude-haiku-4-5');
                  }
                }}
              />
            );
          }
          return null;
        })}
      </div>
    );
  }

  if (block.type === 'error') {
    const errorMessage = formatErrorMessage(block.content || '', block.errorType);
    return (
      <div className="text-destructive" data-msg-id={block.id}>
        <span className="whitespace-pre-wrap break-words">{errorMessage}</span>
        {isLastError && onTryAgain && (
          <span
            className="ml-2 text-xs cursor-pointer hover:underline"
            onClick={onTryAgain}
          >
            (click to retry)
          </span>
        )}
      </div>
    );
  }

  return null;
};